import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { workOrdersAPI, assetsAPI, usersAPI } from '../../api';
import toast from 'react-hot-toast';
import styles from './WorkOrders.module.css';

const PRIORITY_COLORS = { critical:{bg:'#fee2e2',color:'#b91c1c'}, high:{bg:'#fff7ed',color:'#c2410c'}, medium:{bg:'#fef9c3',color:'#a16207'}, low:{bg:'#f1f5f9',color:'#64748b'} };
const STATUS_COLORS   = { open:{bg:'#fff7ed',color:'#c2410c'}, assigned:{bg:'#eff4ff',color:'#1a56db'}, in_progress:{bg:'#f0fdf4',color:'#16a34a'}, on_hold:{bg:'#fef9c3',color:'#a16207'}, closed:{bg:'#dcfce7',color:'#15803d'}, cancelled:{bg:'#f1f5f9',color:'#94a3b8'} };
const TYPE_COLORS     = { pm:{bg:'#eff4ff',color:'#1a56db'}, breakdown:{bg:'#fee2e2',color:'#b91c1c'}, inspection:{bg:'#f0fdf4',color:'#16a34a'}, repair:{bg:'#fff7ed',color:'#c2410c'}, calibration:{bg:'#fdf4ff',color:'#9333ea'} };
const TYPES     = ['pm','breakdown','inspection','repair','calibration'];
const STATUSES  = ['open','assigned','in_progress','on_hold','closed','cancelled'];
const PRIORITIES= ['critical','high','medium','low'];

const LABEL_MAP = {
  // Type
  'pm':'PM', 'breakdown':'Breakdown', 'repair':'Repair',
  'inspection':'Inspection', 'calibration':'Calibration',
  // Status
  'open':'Open', 'closed':'Closed', 'cancelled':'Cancelled',
  'assigned':'Assigned', 'completed':'Completed',
  'in_progress':'In Progress', 'in progress':'In Progress',
  'on_hold':'On Hold',   'on hold':'On Hold',
  // Priority
  'critical':'Critical', 'high':'High', 'medium':'Medium', 'low':'Low',
};
const fmtLabel = v => LABEL_MAP[(v||'').toLowerCase()] ||
  (v||'').replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase());

/* Display WO number — show as-is (series number like 262700001).
   Fallback: plain id if wo_number is empty (legacy records) */
const woDisplay = wo => wo.wo_number
  ? String(wo.wo_number).replace(/^WO-/i, '')   // strip old WO- prefix from legacy records
  : String(wo.id).padStart(9, '0');

function badge(map, val) {
  const s = map[val] || {bg:'#f1f5f9',color:'#64748b'};
  return <span style={{background:s.bg,color:s.color,fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:12,whiteSpace:'nowrap'}}>{fmtLabel(val)}</span>;
}

// ── New WO Modal ──────────────────────────────────────────────
// ── Advanced WO Form Modal ─────────────────────────────────────
// Covers: Report Breakdown, Schedule PM, Request Inspection, Raise Repair WO
function NewWOModal({ assets, users, onClose, onSave, defaultType = 'breakdown' }) {
  const API   = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '');
  const hdrs  = () => ({ Authorization: `Bearer ${localStorage.getItem('iassets_token')}` });

  const WO_TABS = [
    { type:'breakdown',  icon:'⚡', label:'Report Breakdown',   color:'#dc2626', bg:'#fff1f2', border:'#fecaca' },
    { type:'pm',         icon:'🔧', label:'Schedule PM',        color:'#1a56db', bg:'#eff6ff', border:'#bfdbfe' },
    { type:'inspection', icon:'🔍', label:'Request Inspection', color:'#15803d', bg:'#f0fdf4', border:'#bbf7d0' },
    { type:'repair',     icon:'✱',  label:'Raise Repair WO',    color:'#b45309', bg:'#fefce8', border:'#fde68a' },
  ];
  const WO_FIELD = { breakdown:'is_breakdown', pm:'is_schedule_pm', inspection:'is_inspection', repair:'is_repair' };
  const PRIS = ['low','medium','high','critical'];
  const DURATIONS_BD = ['< 2 Hours','2–4 Hours','4–8 Hours','1–3 Days','> 3 Days'];
  const DURATIONS_OT = ['< 2 Hours','2–4 Hours','4–8 Hours','Half Day (4 hrs)','1 Day','2–3 Days','1 Week','> 1 Week'];

  const [woType,         setWoType]         = useState(defaultType);
  const [maintenanceTypes, setMtnTypes]     = useState([]);
  const [failureTypes,   setFailureTypes]   = useState([]);
  const [breakdownId,    setBreakdownId]    = useState('');
  const [maintTypeId,    setMaintTypeId]    = useState('');
  const [failureTypeId,  setFailureTypeId]  = useState('');
  const [assetId,        setAssetId]        = useState('');
  const [priority,       setPriority]       = useState('critical');
  const [assignTo,       setAssignTo]       = useState('');
  const [dueDate,        setDueDate]        = useState(new Date(Date.now()+7*864e5).toISOString().slice(0,16));
  const [duration,       setDuration]       = useState('');
  const [estimatedCost,  setEstimatedCost]  = useState('');
  const [description,    setDescription]    = useState('');
  const [reportedBy,     setReportedBy]     = useState('');
  const [contactNo,      setContactNo]      = useState('');
  const [saving,         setSaving]         = useState(false);

  const isBreakdown = woType === 'breakdown';
  const cfg = WO_TABS.find(t => t.type === woType) || WO_TABS[0];

  // Load masters
  useEffect(() => {
    fetch(`${API}/masters/maintenance-types`, { headers: hdrs() })
      .then(r => r.json()).then(d => setMtnTypes(Array.isArray(d) ? d : [])).catch(()=>{});
    fetch(`${API}/masters/failure-types`, { headers: hdrs() })
      .then(r => r.json()).then(d => setFailureTypes(Array.isArray(d) ? d : [])).catch(()=>{});
  }, []);

  // Fetch Breakdown ID from numbering series
  useEffect(() => {
    if (!isBreakdown) return;
    fetch(`${API}/masters/wo-numbering-series/next-number`, { headers: hdrs() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setBreakdownId(d?.next_number || ''))
      .catch(() => setBreakdownId(''));
  }, [isBreakdown]);

  // Auto-select first matching maintenance type when type changes
  useEffect(() => {
    const field = WO_FIELD[woType];
    const match = maintenanceTypes.find(m => m.is_active && (m[field]===1||m[field]===true));
    setMaintTypeId(match ? String(match.id) : '');
  }, [woType, maintenanceTypes]);

  // Auto-assign from selected asset
  const handleAssetChange = (id) => {
    setAssetId(id);
    const a = assets.find(a => String(a.id) === String(id));
    if (a?.assigned_to) setAssignTo(String(a.assigned_to));
  };

  const filteredMtn = maintenanceTypes.filter(m => {
    if (!m.is_active) return false;
    const f = WO_FIELD[woType]; return !f || m[f]===1 || m[f]===true;
  });

  const submit = async () => {
    if (!assetId)          { toast.error('Asset is required'); return; }
    if (!description.trim()){ toast.error('Description is required'); return; }
    setSaving(true);
    try {
      const a = assets.find(a => String(a.id) === String(assetId));
      const r = await workOrdersAPI.create({
        title:               `${cfg.label} — ${a?.asset_code || ''}`,
        asset_id:            parseInt(assetId),
        type:                woType,
        maintenance_type_id: maintTypeId ? parseInt(maintTypeId) : null,
        priority,
        due_date:            dueDate ? dueDate.slice(0, 10) : null,
        assigned_to:         assignTo ? parseInt(assignTo) : null,
        estimated_cost:      parseFloat(estimatedCost) || 0,
        estimated_duration:  duration || null,
        description,
        ...(isBreakdown && {
          failure_type_id: failureTypeId ? parseInt(failureTypeId) : null,
          reported_by:     reportedBy || null,
          contact_number:  contactNo  || null,
        }),
      });
      toast.success(`Work Order ${r.data.message} created`);
      onSave();
    } catch(e) { toast.error(e?.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  const II = { width:'100%', border:'1.5px solid #e2e8f0', borderRadius:8, padding:'8px 11px',
    fontSize:13, outline:'none', boxSizing:'border-box', background:'#fff' };
  const LL = { fontSize:12, fontWeight:600, color:'#475569', display:'block',
    marginBottom:4, textTransform:'uppercase', letterSpacing:'.04em' };

  return (
    <div className={styles.modal_overlay} onClick={e => e.target===e.currentTarget && onClose()}>
      <div className={styles.modal} style={{ width:640, maxWidth:'95vw', maxHeight:'90vh',
        display:'flex', flexDirection:'column' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className={styles.modal_header}>
          <h2 style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span>{cfg.icon}</span>
            <span>{cfg.label}</span>
          </h2>
          <button onClick={onClose} className={styles.modal_close}>✕</button>
        </div>

        {/* WO Type tabs */}
        <div style={{ display:'flex', gap:6, padding:'10px 20px 0',
          borderBottom:'1px solid #f1f5f9', flexWrap:'wrap' }}>
          {WO_TABS.map(t => (
            <button key={t.type}
              onClick={() => { setWoType(t.type); setPriority(t.type==='breakdown'?'critical':'medium'); }}
              style={{ padding:'7px 14px', border:`1.5px solid ${woType===t.type?t.color:'#e2e8f0'}`,
                borderRadius:'8px 8px 0 0', borderBottom:'none', fontSize:12, fontWeight:700,
                cursor:'pointer', background: woType===t.type ? t.bg : '#fff',
                color: woType===t.type ? t.color : '#64748b',
                marginBottom: woType===t.type ? '-1px' : '0',
                transition:'all .12s' }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className={styles.modal_body} style={{ flex:1, overflowY:'auto' }}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

            {/* Breakdown ID (breakdown only) */}
            {isBreakdown && (
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:8, padding:'10px 14px' }}>
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:'#1a56db',
                    textTransform:'uppercase', letterSpacing:'.06em' }}>Breakdown ID</div>
                  <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>
                    Auto-assigned from WO Numbering Series
                  </div>
                </div>
                <div style={{ fontSize:20, fontWeight:800, color:'#1a56db', fontFamily:'monospace' }}>
                  {breakdownId || <span style={{ fontSize:12, color:'#f59e0b' }}>⚠ Not configured</span>}
                </div>
              </div>
            )}

            {/* Asset */}
            <div>
              <label style={LL}>Asset *</label>
              <select value={assetId} onChange={e => handleAssetChange(e.target.value)} style={II}>
                <option value="">— Select Asset —</option>
                {assets.map(a => <option key={a.id} value={a.id}>{a.asset_code} — {a.name}</option>)}
              </select>
            </div>

            {/* Ticket Type / Maintenance Type */}
            {filteredMtn.length > 0 && (
              <div>
                <label style={LL}>Ticket Type / Maintenance Type</label>
                <select value={maintTypeId} onChange={e => setMaintTypeId(e.target.value)} style={II}>
                  <option value="">— Select Maintenance Type —</option>
                  {filteredMtn.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            )}

            {/* Breakdown section */}
            {isBreakdown && (
              <div style={{ background:'#fff8f8', border:'1px solid #fecaca',
                borderRadius:10, padding:'14px', display:'flex', flexDirection:'column', gap:12 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#dc2626',
                  textTransform:'uppercase', letterSpacing:'.06em' }}>⚡ Breakdown Details</div>

                {failureTypes.length > 0 && (
                  <div>
                    <label style={LL}>Failure Type</label>
                    <select value={failureTypeId} onChange={e => setFailureTypeId(e.target.value)} style={II}>
                      <option value="">— Select Failure Type —</option>
                      {failureTypes.filter(f => f.is_active!==false)
                        .map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </select>
                  </div>
                )}

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <div>
                    <label style={LL}>Reported By</label>
                    <input value={reportedBy} onChange={e => setReportedBy(e.target.value)}
                      placeholder="Name of reporter" style={II}/>
                  </div>
                  <div>
                    <label style={LL}>Contact Number</label>
                    <input value={contactNo} onChange={e => setContactNo(e.target.value)}
                      placeholder="+91 98765 43210" style={II}/>
                  </div>
                </div>
              </div>
            )}

            {/* Priority + Due Date */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={LL}>Priority *</label>
                <select value={priority} onChange={e => setPriority(e.target.value)} style={II}>
                  {PRIS.map(p => <option key={p} value={p}>{fmtLabel(p)}</option>)}
                </select>
              </div>
              <div>
                <label style={LL}>Due Date / Needed By *</label>
                <input type="datetime-local" value={dueDate}
                  onChange={e => setDueDate(e.target.value)} style={II}/>
              </div>
            </div>

            {/* Assign To + Duration */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={{ ...LL, display:'flex', alignItems:'center', gap:6 }}>
                  Assign To
                  {assignTo && <span style={{ fontSize:10, color:'#16a34a', background:'#f0fdf4',
                    padding:'1px 6px', borderRadius:4 }}>✓ From Asset</span>}
                </label>
                <select value={assignTo} onChange={e => setAssignTo(e.target.value)} style={II}>
                  <option value="">— Auto-assign —</option>
                  {users.map(u => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <label style={LL}>Estimated Duration</label>
                <select value={duration} onChange={e => setDuration(e.target.value)} style={II}>
                  <option value="">— Select Duration —</option>
                  {(isBreakdown ? DURATIONS_BD : DURATIONS_OT).map(d =>
                    <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>

            {/* Estimated Cost */}
            <div>
              <label style={LL}>Estimated Cost (₹)</label>
              <input type="number" value={estimatedCost}
                onChange={e => setEstimatedCost(e.target.value)}
                placeholder="0" style={II}/>
            </div>

            {/* Description */}
            <div>
              <label style={LL}>Description / Problem Statement *</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)}
                rows={3} placeholder={isBreakdown
                  ? 'Describe the breakdown — what happened, when, any unusual sounds/smells…'
                  : 'Details about the work required…'}
                style={{ ...II, resize:'vertical', fontFamily:'inherit' }}/>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.modal_footer}>
          <button className={styles.btn_cancel} onClick={onClose}>Cancel</button>
          <button className={styles.btn_submit} onClick={submit} disabled={saving}
            style={{ background: saving ? '#93c5fd' : cfg.color }}>
            {saving ? 'Creating…' : `${cfg.icon} Create Work Order`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Status Update Modal ───────────────────────────────────────
function StatusModal({ wo, onClose, onSave }) {
  const [status, setStatus] = useState(wo.status);
  const [notes, setNotes]   = useState('');
  const [cost,  setCost]    = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try { await workOrdersAPI.updateStatus(wo.id, { status, notes, actual_cost: cost||undefined }); toast.success('Status updated'); onSave(); }
    catch(e) { toast.error('Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className={styles.modal_overlay} onClick={e => e.target === e.currentTarget}>
      <div className={styles.modal} style={{width:420}} onClick={e=>e.stopPropagation()}>
        <div className={styles.modal_header}><h2>Update Status</h2><button onClick={onClose} className={styles.modal_close}>✕</button></div>
        <div className={styles.modal_body}>
          <div className={styles.form_grid}>
            <div className={styles.form_group} style={{gridColumn:'1/-1'}}><label>New Status</label><select value={status} onChange={e=>setStatus(e.target.value)}>{STATUSES.map(s=><option key={s} value={s}>{s.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}</select></div>
            <div className={styles.form_group} style={{gridColumn:'1/-1'}}><label>Actual Cost (₹)</label><input type="number" value={cost} onChange={e=>setCost(e.target.value)} placeholder="0"/></div>
            <div className={styles.form_group} style={{gridColumn:'1/-1'}}><label>Notes</label><textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3} placeholder="Update notes…"/></div>
          </div>
        </div>
        <div className={styles.modal_footer}>
          <button className={styles.btn_cancel} onClick={onClose}>Cancel</button>
          <button className={styles.btn_submit} onClick={submit} disabled={saving}>{saving?'Saving…':'Update Status'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Detail Panel ──────────────────────────────────────────────
function DetailPanel({ wo, onUpdateStatus }) {
  const navigate = useNavigate();
  if (!wo) return <div className={styles.detail_empty}>Select a work order to view details</div>;
  const pc = PRIORITY_COLORS[wo.priority]||{bg:'#f1f5f9',color:'#64748b'};
  const sc = STATUS_COLORS[wo.status]||{bg:'#f1f5f9',color:'#64748b'};
  const tc = TYPE_COLORS[wo.type]||{bg:'#f1f5f9',color:'#64748b'};
  const isOverdue = wo.due_date && new Date(wo.due_date) < new Date() && !['closed','cancelled'].includes(wo.status);
  return (
    <div className={styles.detail_panel}>
      <div className={styles.dp_header}>
        <span className={styles.dp_wo_num}>{woDisplay(wo)}</span>
        <span style={{background:sc.bg,color:sc.color,fontSize:11,fontWeight:600,padding:'3px 9px',borderRadius:12}}>{fmtLabel(wo.status)}</span>
      </div>
      <div className={styles.dp_title}>{wo.title}</div>
      {isOverdue && <div className={styles.dp_overdue}>⚠ Overdue</div>}
      {wo.description && <p className={styles.dp_desc}>{wo.description}</p>}
      <div className={styles.dp_badges}>
        <span style={{background:tc.bg,color:tc.color,fontSize:11,fontWeight:600,padding:'3px 9px',borderRadius:12}}>{fmtLabel(wo.type)}</span>
        <span style={{background:pc.bg,color:pc.color,fontSize:11,fontWeight:600,padding:'3px 9px',borderRadius:12}}>{fmtLabel(wo.priority)}</span>
      </div>
      <div className={styles.dp_actions}>
        <button className={styles.dp_btn_primary} onClick={onUpdateStatus}>⟳ Update Status</button>
        <button className={styles.dp_btn_sec} onClick={()=>navigate(`/work-orders/${wo.id}`)}>View Full →</button>
      </div>
      <div className={styles.dp_divider}/>
      <div className={styles.dp_section}>Work Order Details</div>
      {[['Asset',wo.asset_name],['Asset Code',wo.asset_code],['Location',wo.location_name],['Assigned To',wo.assigned_name],['Due Date',wo.due_date?.slice(0,10)],['Estimated Cost',wo.estimated_cost?'₹'+Number(wo.estimated_cost).toLocaleString('en-IN'):null],['Actual Cost',wo.actual_cost>0?'₹'+Number(wo.actual_cost).toLocaleString('en-IN'):null],['Completed',wo.completed_date?.slice(0,10)],['Created',wo.created_at?.slice(0,10)]].filter(([,v])=>v).map(([l,v])=>(
        <div key={l} className={styles.dp_row}><span>{l}</span><strong>{v}</strong></div>
      ))}
      {wo.notes && (<><div className={styles.dp_divider}/><div className={styles.dp_section}>Notes</div><p className={styles.dp_desc}>{wo.notes}</p></>)}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────
export default function WorkOrdersList() {
  const navigate            = useNavigate();
  const [searchParams]      = useSearchParams();
  const [wos,    setWos]    = useState([]);
  const [assets, setAssets] = useState([]);
  const [users,  setUsers]  = useState([]);
  const [total,  setTotal]  = useState(0);
  const [loading,setLoading]= useState(true);
  const [selected,setSelected]=useState(null);
  const [showNew, setShowNew]=useState(false);
  const [newWoType, setNewWoType]=useState('breakdown');
  const [showStatus,setShowStatus]=useState(false);
  const [showDrop,setShowDrop]=useState(false);
  const dropRef = useRef();
  const [filters,setFilters]= useState({ q:'', type:'', status:searchParams.get('status')||'', priority:'' });

  useEffect(()=>{
    const h=e=>{if(dropRef.current&&!dropRef.current.contains(e.target))setShowDrop(false);};
    document.addEventListener('mousedown',h); return()=>document.removeEventListener('mousedown',h);
  },[]);

  useEffect(()=>{ assetsAPI.list({limit:200}).then(r=>setAssets(r.data.data)).catch(()=>{}); usersAPI.list().then(r=>setUsers(r.data.data)).catch(()=>{}); },[]);

  const load = () => {
    setLoading(true);
    const p=Object.fromEntries(Object.entries(filters).filter(([,v])=>v));
    workOrdersAPI.list(p).then(r=>{
      const raw = r.data.data || [];
      const sorted = [...raw].sort((a,b)=>{
        const da = a.due_date ? new Date(a.due_date) : new Date('9999-12-31');
        const db = b.due_date ? new Date(b.due_date) : new Date('9999-12-31');
        return da - db;
      });
      setWos(sorted);
      setTotal(r.data.total);
    }).catch(console.error).finally(()=>setLoading(false));
  };
  useEffect(()=>{ load(); },[filters]);

  const upd = (k,v) => setFilters(f=>({...f,[k]:v}));
  const countByStatus = wos.reduce((acc,w)=>({...acc,[w.status]:(acc[w.status]||0)+1}),{});
  const STATUS_FILTERS = [
    {key:'',label:'All Work Orders'},{key:'open',label:'Open'},{key:'assigned',label:'Assigned'},
    {key:'in_progress',label:'In Progress'},{key:'on_hold',label:'On Hold'},{key:'closed',label:'Closed'},
  ];

  return (
    <div className={styles.page}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.new_btn_wrap} ref={dropRef}>
          <button className={styles.new_btn} onClick={()=>navigate('/work-orders/new?type=breakdown')}>New</button>
         {/*  <button className={styles.new_btn_arrow} onClick={()=>setShowDrop(d=>!d)}>▾</button> */}
          {showDrop&&(<div className={styles.new_dropdown}>
           {/*  <div className={styles.new_dropdown_item} onClick={()=>{navigate('/work-orders/new?type=breakdown');setShowDrop(false);}}>⚡ Report Breakdown</div>
            <div className={styles.new_dropdown_item} onClick={()=>{navigate('/work-orders/new?type=pm');setShowDrop(false);}}>🔧 Schedule PM</div>
            <div className={styles.new_dropdown_item} onClick={()=>{navigate('/work-orders/new?type=inspection');setShowDrop(false);}}>🔍 Request Inspection</div>
            <div className={styles.new_dropdown_item} onClick={()=>{navigate('/work-orders/new?type=repair');setShowDrop(false);}}>✱ Raise Repair WO</div>
            <div className={styles.new_dropdown_item} onClick={()=>{navigate('/work-orders/new');setShowDrop(false);}}>📋 Full Advanced Form</div> */}
          </div>)}
        </div>
        <div className={styles.search_wrap}>
          <span className={styles.search_icon}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
          <input className={styles.search_input} placeholder="Search work orders…" value={filters.q} onChange={e=>upd('q',e.target.value)}/>
          {filters.q&&<button className={styles.search_clear} onClick={()=>upd('q','')}>✕</button>}
        </div>
        <select className={styles.toolbar_select} value={filters.type} onChange={e=>upd('type',e.target.value)}>
          <option value="">All Types</option>{TYPES.map(t=><option key={t} value={t}>{fmtLabel(t)}</option>)}
        </select>
        <select className={styles.toolbar_select} value={filters.priority} onChange={e=>upd('priority',e.target.value)}>
          <option value="">All Priorities</option>{PRIORITIES.map(p=><option key={p} value={p}>{fmtLabel(p)}</option>)}
        </select>
        <div className={styles.toolbar_right}><span className={styles.total_badge}>{total} orders</span></div>
      </div>

      <div className={styles.body}>
        {/* Left sidebar */}
        <div className={styles.filter_col}>
          {STATUS_FILTERS.map(f=>{
            const cnt=f.key?(countByStatus[f.key]||0):total;
            const sc=STATUS_COLORS[f.key]||{color:'#1a56db'};
            return(
            <div key={f.key} className={`${styles.filter_item} ${filters.status===f.key?styles.filter_active:''}`} onClick={()=>upd('status',f.key)}>
              <span className={styles.filter_dot} style={{background:f.key?sc.color:'#1a56db'}}/>
              <span className={styles.filter_label}>{f.label}</span>
              <span className={`${styles.filter_count} ${filters.status===f.key?styles.filter_count_active:''}`}>{cnt}</span>
            </div>);
          })}
          <div className={styles.filter_divider}/>
          <div className={styles.filter_section}>By Type</div>
          {TYPES.map(t=>{
            const tc=TYPE_COLORS[t]||{color:'#64748b'};
            return(
            <div key={t} className={`${styles.filter_item} ${filters.type===t?styles.filter_active:''}`} onClick={()=>upd('type',filters.type===t?'':t)}>
              <span className={styles.filter_dot} style={{background:tc.color}}/>
              <span className={styles.filter_label}>{fmtLabel(t)}</span>
            </div>);
          })}
          {Object.values(filters).some(Boolean)&&<button className={styles.reset_btn} onClick={()=>setFilters({q:'',type:'',status:'',priority:''})}>✕ Reset Filters</button>}
        </div>

        {/* Center list */}
        <div className={styles.list_col}>
          {loading?<div className={styles.loading}>Loading…</div>
          :wos.length===0?<div className={styles.empty}><div style={{fontSize:40,marginBottom:12}}>🔧</div><div style={{fontWeight:600,color:'#1e2a3b',marginBottom:6}}>No work orders found</div></div>
          :wos.map(wo=>{
            const pc=PRIORITY_COLORS[wo.priority]||{bg:'#f1f5f9',color:'#64748b'};
            const sc=STATUS_COLORS[wo.status]||{bg:'#f1f5f9',color:'#64748b'};
            const tc=TYPE_COLORS[wo.type]||{bg:'#f1f5f9',color:'#64748b'};
            const isOverdue=wo.due_date&&new Date(wo.due_date)<new Date()&&!['closed','cancelled'].includes(wo.status);
            const isSel=selected?.id===wo.id;
            return(
            <div key={wo.id} className={`${styles.card} ${isSel?styles.card_active:''} ${isOverdue?styles.card_overdue:''}`} onClick={()=>setSelected(wo)}>
              <div className={styles.card_stripe} style={{background:pc.color}}/>
              <div className={styles.card_body}>
                <div className={styles.card_top}>
                  <span className={styles.card_wo_num}>{woDisplay(wo)}</span>
                  <div style={{display:'flex',gap:5}}>
                    <span style={{background:tc.bg,color:tc.color,fontSize:11,fontWeight:600,padding:'2px 7px',borderRadius:10}}>{fmtLabel(wo.type)}</span>
                    <span style={{background:sc.bg,color:sc.color,fontSize:11,fontWeight:600,padding:'2px 7px',borderRadius:10}}>{fmtLabel(wo.status)}</span>
                  </div>
                </div>
                <div className={styles.card_title}>{wo.title}</div>
                <div className={styles.card_meta}>{[wo.asset_name,wo.asset_code,wo.location_name].filter(Boolean).join(' · ')}</div>
                <div className={styles.card_footer}>
                  <span className={styles.card_assign}>{wo.assigned_name||'Unassigned'}</span>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    {isOverdue&&<span style={{fontSize:11,color:'#ef4444',fontWeight:600}}>⚠ Overdue</span>}
                    {wo.due_date&&<span style={{fontSize:12,color:isOverdue?'#ef4444':'#94a3b8'}}>Due: {wo.due_date.slice(0,10)}</span>}
                    <span style={{background:pc.bg,color:pc.color,fontSize:10,fontWeight:700,padding:'1px 6px',borderRadius:10}}>{fmtLabel(wo.priority)}</span>
                  </div>
                </div>
              </div>
              <div className={styles.card_actions}>
                <button className={styles.card_action_btn} title="Update Status" onClick={e=>{e.stopPropagation();setSelected(wo);setShowStatus(true);}}>⟳</button>
                <button className={styles.card_action_btn} title="View" onClick={e=>{e.stopPropagation();navigate(`/work-orders/${wo.id}`);}}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                </button>
              </div>
            </div>);
          })}
        </div>

        {/* Right detail */}
        <div className={styles.detail_col}>
          <DetailPanel wo={selected} onUpdateStatus={()=>setShowStatus(true)}/>
        </div>
      </div>

      {showNew&&<NewWOModal assets={assets} users={users} defaultType={newWoType} onClose={()=>setShowNew(false)} onSave={()=>{setShowNew(false);load();}}/>}
      {showStatus&&selected&&<StatusModal wo={selected} onClose={()=>setShowStatus(false)} onSave={()=>{setShowStatus(false);load();}}/>}
    </div>
  );
}
